import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Section } from './DetailPrimitives';
import { ParameterLine } from './PolicyParametersSection';
import {
    SCOPE_LEVEL_LABEL, browseHref, groupParametersBy, policyHref, scopeLevelColor,
} from './ontologyFormat';
import { normalizeList, ontologyErrorMessage, parametersApi } from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The thresholds that set this concept, grouped by the policy that sets them.
 *
 * Concept → Parameter is the other true 1:N — every parameter names exactly one
 * conceptCode — so reading down from a concept is as unambiguous as reading down from a
 * policy. The two drill-downs are peers, not levels of one hierarchy: a concept can hold
 * parameters under SEVERAL policies, which is exactly why this cannot be folded into the
 * policy view as a sub-level.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO: pick a winner. Groups are ordered by the published
 * scopeLevel precedence, and each parameter carries the server's in_force verdict — but
 * which value a node applies also depends on scope specificity against the execution
 * context, and that scoring stays server-side. Resolution already exists twice; a third
 * implementation here is how a screen ends up confidently showing a threshold the
 * pipeline does not use.
 */
const ConceptParametersSection: React.FC<{ conceptCode: string }> = ({ conceptCode }) => {
    const [parameters, setParameters] = useState<Record<string, any>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!conceptCode) return undefined;
        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                // ?concept=, which applies no in-force clause — drafts are most of what a
                // reviewer comes here to see.
                const res = await parametersApi.list(
                    { concept: conceptCode, limit: 200 }, { signal: controller.signal });
                if (!active) return;
                setParameters(normalizeList(res, 'parameters').documents);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Error loading concept parameters:', err);
                setError(ontologyErrorMessage(err, 'Could not load thresholds for this concept.'));
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [conceptCode]);

    if (loading) {
        return (
            <Section title="Thresholds set for this concept" always>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: dsVar('fg-2') }}>
                    <CircularProgress size={14} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.8125rem' }}>Loading thresholds…</Typography>
                </Box>
            </Section>
        );
    }

    if (error) {
        return (
            <Section title="Thresholds set for this concept" always>
                <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCA5A5') }}>{error}</Typography>
            </Section>
        );
    }

    // Nothing configured is a normal state, not an error: most concepts are vocabulary
    // rather than tunables, and saying so beats an empty heading.
    if (parameters.length === 0) {
        return (
            <Section title="Thresholds set for this concept" always>
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                    No policy sets a threshold for this concept.
                </Typography>
            </Section>
        );
    }

    const groups = groupParametersBy(parameters, 'policy_code');
    const spansPolicies = groups.length > 1;

    return (
        <Section title="Thresholds set for this concept" always>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {groups.map((group) => {
                    const scopeLevel = group.items[0]?.scope_level;
                    const href = policyHref(group.key);

                    return (
                        <Box key={group.key}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.6875rem', fontWeight: 600,
                                        letterSpacing: '0.05em', textTransform: 'uppercase',
                                        color: ink(scopeLevelColor(scopeLevel)),
                                    }}
                                >
                                    {SCOPE_LEVEL_LABEL[scopeLevel ?? ''] ?? scopeLevel ?? 'Unscoped'}
                                </Typography>
                                {href ? (
                                    <Box
                                        component="a"
                                        href={href}
                                        sx={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: '0.75rem', color: dsVar('fg-2'),
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline', color: dsVar('accent-fg') },
                                        }}
                                    >
                                        {group.key}
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontFamily: '"JetBrains Mono", monospace',
                                            fontSize: '0.75rem', color: dsVar('fg-2'),
                                        }}
                                    >
                                        {group.key}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ pl: 1.5, borderLeft: `2px solid ${scopeLevelColor(scopeLevel)}33` }}>
                                {group.items.map((parameter, index) => (
                                    <ParameterLine
                                        key={parameter.parameter_code ?? index}
                                        parameter={parameter}
                                    />
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Only worth saying when there is genuinely a contest to resolve. */}
            {spansPolicies && (
                <Box
                    sx={{
                        mt: 2, p: 1.25, borderRadius: '6px',
                        backgroundColor: 'rgba(125, 211, 252, 0.06)',
                        border: '1px solid rgba(125, 211, 252, 0.25)',
                    }}
                >
                    <Typography sx={{ fontSize: '0.8125rem', color: ink('#BAE6FD'), lineHeight: 1.6 }}>
                        More than one policy sets this concept. An organisation value takes
                        precedence over a regulatory baseline, but which one a node actually
                        applies also depends on the execution scope — segment, product,
                        jurisdiction and activity.
                    </Typography>
                    <Box
                        component="a"
                        href={browseHref('parameters', { concept: conceptCode })}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75,
                            fontSize: '0.8125rem', color: ink('#7DD3FC'), textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Compare every parameter for this concept
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                </Box>
            )}
        </Section>
    );
};

export default ConceptParametersSection;
